import { format, parseISO } from "date-fns";
import type { Game } from "@/types";
import { withCacheBuster } from "./share-intents";

/** Discreet single-line cross-promo footer appended to every outgoing share. */
const SHARE_FOOTER = "— Powered by Vote.GameOn · free for grassroots ⚽";

function withFooter(body: string): string {
  return `${body}\n\n${SHARE_FOOTER}`;
}

/**
 * Builds a WhatsApp/iMessage/Telegram-friendly share message for an MVP vote link.
 * The shareUrl is included at the end. Never includes any private group URL.
 */
/** Short team name prefix used in share headers (helps a coach who manages
 *  multiple teams confirm they're pasting into the right WhatsApp group). */
function teamPrefix(teamName?: string | null): string {
  const t = (teamName || "").trim();
  if (!t) return "";
  const short = t.length > 28 ? `${t.slice(0, 28).trimEnd()}…` : t;
  return `${short} · `;
}

export function buildMvpShareText(
  game: Game | null | undefined,
  shareUrl: string,
  teamName?: string | null,
): string {
  const url = withCacheBuster(shareUrl);
  const prefix = teamPrefix(teamName);
  if (!game) return withFooter(`🗳️ ${prefix}MVP VOTE\n${url}`);
  let dateStr = "";
  try {
    dateStr = format(parseISO(game.date), "EEE, MMM d");
  } catch {
    dateStr = game.date;
  }
  const timeStr = game.match_time ? `, ${game.match_time}` : "";
  const opp = game.opponent || "";
  const shortOpp = opp.length > 10 ? `${opp.slice(0, 10).trimEnd()}…` : opp;
  const locStr = game.match_location ? ` · ${game.match_location}` : "";
  const header = `🗳️ ${prefix}MVP VOTE · vs ${shortOpp} · ${dateStr}${timeStr}${locStr}`;
  return withFooter(`${header}\n\nVote for your top players:\n${url}`);
}

/**
 * Builds an RSVP share message.
 */
export function buildRsvpShareText(
  game: Game | null | undefined,
  shareUrl: string,
  teamName?: string | null,
): string {
  const url = withCacheBuster(shareUrl);
  const prefix = teamPrefix(teamName);
  if (!game) return withFooter(`📋 ${prefix}RSVP\n${url}`);
  let dateStr = "";
  try {
    dateStr = format(parseISO(game.date), "EEE, MMM d");
  } catch {
    dateStr = game.date;
  }
  const timeStr = game.match_time ? `, ${game.match_time}` : "";
  const opp = game.opponent || "";
  const locStr = game.match_location ? ` · ${game.match_location}` : "";
  const header = `📋 ${prefix}RSVP · vs ${opp} · ${dateStr}${timeStr}${locStr}`;
  return withFooter(`${header}\n\nLet us know if you can make it:\n${url}`);
}

/**
 * Builds an attendance share message (training roll-call or match attendance view).
 */
export function buildAttendanceShareText(teamName: string, shareUrl: string): string {
  const url = withCacheBuster(shareUrl);
  const header = `📋 ATTENDANCE · ${teamName || "Your team"}`;
  return withFooter(`${header}\n\nSee who's in this week:\n${url}`);
}

/**
 * Builds an autopilot share message (fixture import landing).
 */
export function buildAutopilotShareText(shareUrl: string): string {
  const url = withCacheBuster(shareUrl);
  const header = `🤖 FIXTURE AUTOPILOT · Vote.GameOn`;
  return withFooter(`${header}\n\nAuto-import your league fixtures — stop typing games:\n${url}`);
}

/**
 * Builds an RPE share message (load-monitoring response link).
 */
export function buildRpeShareText(shareUrl: string, sessionLabel?: string): string {
  const url = withCacheBuster(shareUrl);
  const header = sessionLabel
    ? `💪 SESSION RPE · ${sessionLabel}`
    : `💪 SESSION RPE`;
  return withFooter(`${header}\n\nHow hard was today's session? Tap a number 1–10:\n${url}`);
}
